import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@/shared/ui';

import { templateEditorMessages } from '../../config';
import { EditorButton } from '../EditorButton';

import styles from './EditorHeader.module.scss';

type EditorHeaderProps = {
  title: string;
  readOnly: boolean;
  canImport: boolean;
  canExport: boolean;
  canSave: boolean;
  isSystem: boolean;
  checklist?: ReactNode;
  className?: string;
  onBack: () => void;
  onEditDetails: () => void;
  onImport: () => void;
  onExport: () => void;
  onSave: () => void;
  onClone: () => void;
};

export const EditorHeader: FC<EditorHeaderProps> = (props) => {
  const {
    title,
    readOnly,
    canImport,
    canExport,
    canSave,
    isSystem,
    checklist,
    className,
    onBack,
    onEditDetails,
    onImport,
    onExport,
    onSave,
    onClone,
  } = props;

  const { formatMessage } = useIntl();

  const editDetailsLabel = formatMessage(
    templateEditorMessages.editTemplateDetails,
  );

  return (
    <div
      className={cn(styles.container, className)}
      data-component="template-editor-header"
    >
      <button type="button" className={styles.backLink} onClick={onBack}>
        <Icon name="arrowDown" size={16} className={styles.backIcon} />
        {formatMessage(templateEditorMessages.allTemplatesLink)}
      </button>

      <div className={styles.titleRow}>
        <h2 className={styles.title} title={title}>
          {title}
        </h2>
        <EditorButton
          variant="icon"
          className={styles.editButton}
          title={editDetailsLabel}
          aria-label={editDetailsLabel}
          onClick={onEditDetails}
        >
          <Icon name="pen" size={24} />
        </EditorButton>
        {isSystem && (
          <span className={cn('p4b', styles.systemBadge)}>
            {formatMessage(templateEditorMessages.systemBadge)}
          </span>
        )}

        <div className={styles.spacer} />

        <div className={styles.actions}>
          {readOnly && (
            <span className={cn('p4', styles.readOnlyHint)}>
              {formatMessage(templateEditorMessages.systemTemplateReadOnly)}
            </span>
          )}
          {checklist}
          {canImport && (
            <EditorButton variant="hairline" onClick={onImport}>
              {formatMessage(templateEditorMessages.importButton)}
            </EditorButton>
          )}
          <EditorButton
            variant="hairline"
            disabled={!canExport}
            onClick={onExport}
          >
            {formatMessage(templateEditorMessages.exportButton)}
          </EditorButton>
          {readOnly ? (
            <Button variant="primary" size="medium" onClick={onClone}>
              {formatMessage(templateEditorMessages.cloneToOrganization)}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="medium"
              disabled={!canSave}
              onClick={onSave}
            >
              {formatMessage(templateEditorMessages.save)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
